<template>
  <div>
    <dp-container v-if="loading || !ready">
      <dp-loading />
    </dp-container>
    <dp-container v-else-if="windowWidth > 991 && productStyle && productStyle.id" :class="{ disabled: itemLoading }">
      <!-- Desktop Screen -->
      <ValidationObserver ref="p_style_form" slim>
        <form class="mx-4 mt-4">
          <!-- NASN cohort: keep the back link, drop the avatar/name/price header -->
          <section v-if="showNasnStyleHeader" class="mb-3">
            <ProductConfigBackLink :pet-id="petId" product-type="Tag" />
          </section>
          <!-- NASN cohort: Figma "[Pet Name]'s Free Tag" welcome header -->
          <section v-if="showNasnStyleHeader" class="nasn-style-header">
            <h1 class="nasn-style-header__title">{{ $t('product.nasn.title', { petName: nasnPetName }) }}</h1>
            <p class="nasn-style-header__subtitle" v-html="$t('product.nasn.subtitle')"></p>
          </section>
          <template v-else>
            <section v-if="!isAdmin || (isAdmin && !$router.currentRoute.query['replacement-tag']) || determineRedirectRoute" class="mb-3">
              <ProductConfigBackLink :pet-id="petId" product-type="Tag" />
            </section>
            <pet-header
              v-if="requiredPet"
              :is-admin="isAdmin"
              :pet="requiredPet"
              :variant="editTagRehydrated ? 'editTag' : 'default'"
              class="mt-2 mb-4"
            />
            <div v-if="showSkipReplacementTagBanner">
              <div class="h4 text-center text-dark m-0 font-weight-bold d-block align-items-center mb-5" style="font-size: 24px">
                <p>{{ $t(`petHeader.skipLicensing.text.default`, { petName: requiredPet.name }) }}</p>
                <b-button variant="dplight" @click="orderNoTag">{{ $t('petHeader.skipLicensing.linkText') }}</b-button>
              </div>
            </div>
            <h1 class="text-center" v-html="productStyleName"></h1>
            <h2
              class="text-center text-dark h3 mx-3 px-lg-5"
              :class="isAdmin ? 'admin-user' : 'po-user'"
              v-html="productStyleDescription"
            />

            <div v-if="selectedProduct && selectedProduct.price">
              <h2 v-if="selectedProduct.discountedPrice" class="text-center">
                <del>${{ selectedProduct.price.toFixed(2) }}</del>
                <span class="ml-2 discounted-price">
                  <span class="sr-only">{{ $t('dp.generic.priceReducedTo') }} </span> ${{ selectedProduct.discountedPrice.toFixed(2) }}
                </span>
              </h2>
              <h2 v-else-if="selectedProduct.price" class="text-center">${{ selectedProduct.price.toFixed(2) }}</h2>
            </div>
            <div v-else>
              <h2 v-if="productStyle && productStyle.discountedPrice" class="text-center">
                <del>${{ productStyle.price.toFixed(2) }}</del>
                <span class="ml-2 discounted-price">
                  <span class="sr-only">{{ $t('dp.generic.priceReducedTo') }} </span> ${{ productStyle.discountedPrice.toFixed(2) }}
                </span>
              </h2>
              <h2 v-else-if="productStyle && productStyle.price" class="text-center">${{ productStyle.price.toFixed(2) }}</h2>
            </div>
          </template>
          <p v-if="productBundleCallToActionText" class="text-center text-brand-dark">
            <span v-html="productBundleCallToActionText"></span>
            <br />
            <a class="text-brand-dark text-decoration-underline ml-1" href="#product-bundle-sidebar">{{
              $t('product.productBundleCallToActionLink')
            }}</a>
          </p>
          <b-row class="mt-lg-5">
            <!-- Desktop Preview Column -->
            <b-col :sm="12" :md="12" :lg="5" :xl="5">
              <carousel
                v-show="(!isDYOT || !color || !size || !design) && (isAdmin || [1, 3].includes(currentStep))"
                :images="images"
                :auto-slide="!color"
                :curr-image-idx="curImageIdx"
                class="mt-sm-4 mt-md-4 mt-lg-0 mt-xl-0 mb-sm-4 mb-md-4 mb-lg-0 mb-xl-0 sticky"
              />
              <b-row v-if="!isAdmin" :key="`Preview_${currentStep}`" class="sticky" style="background-color: #f7f7f7" no-gutters>
                <b-col v-show="color && size && design && [1, 3].includes(currentStep)" :cols="currentStep === totalSteps ? 6 : 12">
                  <custom-design
                    v-if="isDYOT"
                    ref="Preview_Front"
                    :key="`Preview_Front_${color}_${size}_${design}_${currentStep}x${windowWidth}x${
                      designerModeConfig && designerModeConfig.designerMode
                    }`"
                    v-model="designerModeConfig"
                    :title="currentStep === 3 ? 'FRONT' : null"
                    canvas-b-g-color="#f7f7f7"
                    :scale="0.8"
                    :height="500"
                    :boundary-padding="25"
                    :design-svg-img-path="designSvgImgPath ? getImage(designSvgImgPath) : false"
                    :base-tag-svg-img-path="baseTagSvgImgPath ? getImage(baseTagSvgImgPath) : false"
                    :pet-name-text="designTextConfig.petNameText"
                    :phone-text="designTextConfig.phoneText"
                    :home-safe-id-text="designTextConfig.homeSafeIdText || false"
                    :exportable="true"
                    :preview="currentStep === 3"
                    class="mt-sm-4 mt-md-4 mt-lg-0 mt-xl-0 mb-sm-4 mb-md-4 mb-lg-0 mb-xl-0"
                  />
                </b-col>
                <b-col v-show="currentStep === 2 || (currentStep === 3 && isDYOT)" :cols="currentStep === totalSteps ? 6 : 12">
                  <ValidationObserver :ref="`p_style_form_step_${!isDYOT ? 2 : 4}`" v-slot="{ validated }" slim>
                    <personalize
                      :key="`Preview_Back_${currentStep}x${windowWidth}`"
                      v-model="personalize"
                      :preview-only="true"
                      :preview-height="500"
                      :pet-known="!!requiredPet"
                      :pet="requiredPet ? requiredPet : null"
                      :is-logged-in="!!user && !!user.id"
                      :pet-owner="order ? order.petOwner : null"
                      :preview-show-labels="currentStep !== totalSteps"
                      :base-svg-img-path="baseTagSvgImgPath"
                      :back-svg-img-path="backSvgImgPath"
                      :default-phone-number="defaultPhoneNumber"
                      :show-redesigned-tag-back="showRedesignedTagBack"
                      :left-zone-line1="tagBackLeftZoneLine1"
                      :left-zone-line2="tagBackLeftZoneLine2"
                      :hotline-number="tagBackHotlineNumber"
                      :tag-back-url="tagBackUrl"
                      :is-admin="isAdmin"
                      :price="personalizationPrice"
                      :validated="validated"
                      @assignToPet="assignToPet"
                    />
                  </ValidationObserver>
                </b-col>
              </b-row>
            </b-col>
            <b-col :sm="12" :md="12" :lg="6" :xl="6" :offset-lg="1" :offset-xl="1">
              <!-- Step 1 (Select Tag Colour, Size, IF DYO ? Design, Add Text and Select Font) -->
              <section v-show="isAdmin || !isDYOT || currentStep === 1" class="mb-5">
                <ValidationObserver ref="p_style_form_step_1" slim>
                  <color-select
                    v-if="colorOptions.length > 1 || (colorOptions.length == 1 && colorOptions[0].name != '')"
                    v-model="color"
                    class="mb-4"
                    :options="colorOptions"
                    @change="onColorChange"
                  />
                  <size-select
                    ref="size-select"
                    v-model="size"
                    :disable-large="disable.large"
                    :disable-small="disable.small"
                    class="mb-4"
                  />
                  <section v-if="isDYOT">
                    <design-select
                      :key="`Design_Select_${color}x${windowWidth}`"
                      v-model="design"
                      class="my-4"
                      :options="designOptions"
                      :base-tag-svg-img-path="baseTagSvgImgPath"
                    />
                    <design-font-select v-model="designFont" class="mb-4" />
                    <design-text
                      v-model="designText"
                      class="my-4"
                      :text-before-valid="!!color && !!size"
                      :show-text2="!!designTextConfig.phoneText"
                      @increaseFontSize="increaseFontSize"
                      @reduceFontSize="reduceFontSize"
                      @moveUp="moveUp"
                      @moveDown="moveDown"
                      @moveLeft="moveLeft"
                      @moveRight="moveRight"
                    />
                  </section>
                </ValidationObserver>
              </section>
              <!-- Step 2 (Add Personalization) -->
              <section v-show="isAdmin || !isDYOT || currentStep === 2">
                <ValidationObserver ref="p_style_form_step_2" v-slot="{ validated }" slim>
                  <personalize
                    :key="`Preview_Back_${personalize.editTag}`"
                    v-model="personalize"
                    :pet-known="!!requiredPet"
                    :pet="requiredPet ? requiredPet : null"
                    :is-logged-in="!!user && !!user.id"
                    :pet-owner="order ? order.petOwner : null"
                    :base-svg-img-path="false"
                    :back-svg-img-path="false"
                    :hide-preview="true"
                    :default-phone-number="defaultPhoneNumber"
                    :show-redesigned-tag-back="showRedesignedTagBack"
                    :left-zone-line1="tagBackLeftZoneLine1"
                    :left-zone-line2="tagBackLeftZoneLine2"
                    :hotline-number="tagBackHotlineNumber"
                    :tag-back-url="tagBackUrl"
                    class="mb-4"
                    :is-admin="isAdmin"
                    :price="personalizationPrice"
                    :validated="validated"
                    @assignToPet="assignToPet"
                  />
                </ValidationObserver>
              </section>
              <!-- Step 3 (Add Glow Tag and/or Select Connector) -->
              <section v-show="isAdmin || !isDYOT || currentStep === 3">
                <ValidationObserver ref="p_style_form_step_3" slim>
                  <h1 v-if="currentStep === 3" class="font-weight-bold">{{ $t('dp.generic.almostDone') }}</h1>
                  <glow-tag-product-select
                    v-show="!isAdmin && showGlowTagProduct"
                    ref="glow-tag-product-select"
                    :key="`GlowTag_${glowTagQuantity}`"
                    v-model="glowTagQuantity"
                    :glow-tag-product-id="getGlowTagProductId()"
                    class="mt-4 mb-4"
                  />
                  <connector-select
                    v-show="showConnectorProduct"
                    ref="connector-select"
                    v-model="connector"
                    :is-admin="isAdmin"
                    :options="connectorOptions"
                    class="mb-4"
                  />
                  <key-chain-product-select
                    v-show="!isAdmin && showKeychainProduct"
                    ref="key-chain-product-select"
                    v-model="keyChain"
                    :key-chain-product="keyChainProduct"
                    class="mb-4"
                  />
                  <!-- Step 4 (Add personalized wallet card) -->
                  <wallet-card-product-select
                    v-if="showWalletCardCTA"
                    ref="wallet-card-product-select"
                    v-model="optInForWalletCard"
                    :image="walletCardImage"
                    :wallet-card-branding="walletCardBranding"
                    class="mb-4 pt-2"
                  />
                </ValidationObserver>
              </section>
              <Steps
                v-if="!isAdmin && isDYOT"
                :current-step="currentStep"
                :total-steps="totalSteps"
                :is-button-disabled="itemLoading"
                style="background-color: transparent"
                @prevClick="prevClick"
                @nextClick="nextClick"
              >
                <template #prev-btn-content>
                  <section v-if="currentStep !== totalSteps" class="float-left">&lt; {{ $t('dp.generic.previous') }}</section>
                  <section v-else class="float-left" @click="currentStep = 1">&lt; {{ $t('editTag') }}</section>
                </template>
                <template #next-btn-content>
                  <section v-if="currentStep !== totalSteps">{{ $t('dp.generic.next') }}</section>
                  <section v-else>
                    {{ editTagRehydrated ? $t('product.updateTag') : submitButtonComputedText }}
                    <div v-if="itemLoading" role="status" class="fa-pulse float-right mt-1">
                      <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
                      <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
                    </div>
                  </section>
                </template>
              </Steps>

              <product-bundle-sidebar-element
                v-if="!forceHideBundledSidebar && !isEditOrderItemBundleTag && showProductBundleSidebarElement"
                id="product-bundle-sidebar"
                :context="productStyle.productBundleSidebarElement"
                :submit-disabled="allOutOfStock || itemLoading"
                :item-loading="itemLoading"
                class="my-4"
                @bundle-component-created="bundleComponentCreated"
                @submit="validateBeforeAddBundle"
              />

              <b-button
                v-if="isAdmin || !isDYOT"
                id="product-style-add-to-basket"
                v-testid="'tag-add-to-basket-action'"
                variant="primary"
                :disabled="allOutOfStock || itemLoading"
                block
                class="py-3 rounded-8px"
                @click="validateBeforeAddItem"
              >
                {{ editTagRehydrated ? $t('product.updateTag') : !isAdmin ? submitButtonComputedText : $t('product.addToOrder') }}
                <div v-if="itemLoading" role="status" class="fa-pulse float-right mt-1">
                  <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
                  <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
                </div>
              </b-button>

              <!-- DISCLAIMER -->
              <small v-if="productStyle && productStyle.disclaimer" class="d-block mt-3" v-html="productStyle.disclaimer" />
            </b-col>
          </b-row>
        </form>
      </ValidationObserver>
    </dp-container>
    <dp-container
      v-else-if="windowWidth <= 991 && productStyle && productStyle.id"
      class="dp-container-wrapper"
      :class="{ disabled: itemLoading }"
    >
      <!--  Mobile Screen -->
      <ValidationObserver ref="p_style_form" slim>
        <form class="mx-4">
          <!-- Step 1 (Select Tag Colour & Size) -->
          <section v-show="!isDYOT || currentStep === 1" class="mt-4">
            <!-- NASN cohort: keep the back link, drop the avatar/name/price header -->
            <section v-if="showNasnStyleHeader" class="mb-3">
              <b-link
                :to="!petId ? { name: 'Store' } : { name: 'StoreForPet', params: { petId } }"
                class="text-decoration-none"
                v-text="'< Back to Store'"
              />
            </section>
            <!-- NASN cohort: Figma "[Pet Name]'s Free Tag" welcome header -->
            <section v-if="showNasnStyleHeader" class="nasn-style-header">
              <h1 class="nasn-style-header__title">{{ $t('product.nasn.title', { petName: nasnPetName }) }}</h1>
              <p class="nasn-style-header__subtitle" v-html="$t('product.nasn.subtitle')"></p>
            </section>
            <template v-else>
              <section class="mb-3">
                <b-link
                  :to="!petId ? { name: 'Store' } : { name: 'StoreForPet', params: { petId } }"
                  class="text-decoration-none"
                  v-text="'< Back to Store'"
                />
              </section>
              <pet-header v-if="requiredPet" class="mt-2 mb-4" :pet="requiredPet" :is-admin="isAdmin" />
              <div v-if="showSkipReplacementTagBanner">
                <div class="h4 text-center text-dark m-0 font-weight-bold d-block align-items-center mb-5" style="font-size: 24px">
                  <p>{{ $t(`petHeader.skipLicensing.text.default`, { petName: requiredPet.name }) }}</p>
                  <b-button variant="dplight" @click="orderNoTag">{{ $t('petHeader.skipLicensing.linkText') }}</b-button>
                </div>
              </div>
              <h1 class="text-center" v-html="productStyleName"></h1>
              <h2 class="text-center text-dark h3 mx-3" :class="isAdmin ? 'admin-user' : 'po-user'" v-html="productStyleDescription" />
              <h2 v-if="productStyle.discountedPrice" class="text-center">
                <del>${{ productStyle.price.toFixed(2) }}</del>
                <span class="ml-2 discounted-price">
                  <span class="sr-only">{{ $t('dp.generic.priceReducedTo') }}</span> ${{ productStyle.discountedPrice.toFixed(2) }}
                </span>
              </h2>
              <h2 v-else-if="productStyle && productStyle.price" class="text-center">${{ productStyle.price.toFixed(2) }}</h2>
            </template>

            <p v-if="productBundleCallToActionText" class="text-center text-brand-dark">
              <span v-html="productBundleCallToActionText"></span>
              <a class="text-brand-dark text-decoration-underline ml-1" href="#product-bundle-sidebar">{{
                $t('product.productBundleCallToActionLink')
              }}</a>
            </p>

            <b-row class="mt-lg-5">
              <b-col :sm="12" :md="12" :lg="5" :xl="5">
                <carousel
                  :images="images"
                  :auto-slide="!color"
                  :curr-image-idx="curImageIdx"
                  class="mt-sm-4 mt-md-4 mt-lg-0 mt-xl-0 mb-sm-4 mb-md-4 mb-lg-0 mb-xl-0"
                />
              </b-col>
              <b-col :sm="12" :md="12" :lg="6" :xl="6" :offset-lg="1" :offset-xl="1">
                <ValidationObserver ref="p_style_form_step_1" slim>
                  <color-select
                    v-if="colorOptions.length > 1 || (colorOptions.length === 1 && colorOptions[0].name != '')"
                    v-model="color"
                    class="mt-4 mt-lg-0 mb-4"
                    :options="colorOptions"
                    @change="onColorChange"
                  />
                  <size-select
                    ref="size-select"
                    v-model="size"
                    :disable-large="disable.large"
                    :disable-small="disable.small"
                    class="mb-4"
                  />
                </ValidationObserver>
              </b-col>
            </b-row>
          </section>
          <!-- If DYO -->
          <section v-if="isDYOT && [2, 3].includes(currentStep)">
            <section :class="windowWidth < windowHeight ? 'sticky bg-white mx-n3-5' : ''">
              <custom-design
                ref="Preview_Front"
                :key="`Preview_Front_${currentStep}x${windowWidth}x${designerModeConfig}`"
                v-model="designerModeConfig"
                :scale="windowWidth < 450 ? 0.65 : 1"
                :height="275"
                :boundary-padding="25"
                canvas-b-g-color="#f7f7f7"
                :pet-name-text="designTextConfig.petNameText"
                :phone-text="designTextConfig.phoneText"
                :home-safe-id-text="designTextConfig.homeSafeIdText || false"
                :exportable="true"
                class="mt-sm-4 mt-md-4 mt-lg-0 mt-xl-0 mb-sm-4 mb-md-4 mb-lg-0 mb-xl-0"
                :base-tag-svg-img-path="baseTagSvgImgPath ? getImage(baseTagSvgImgPath) : false"
                :design-svg-img-path="designSvgImgPath ? getImage(designSvgImgPath) : false"
              />
            </section>
            <!-- Step 2 (Select Tag Design) -->
            <section v-show="currentStep === 2">
              <ValidationObserver ref="p_style_form_step_2" slim>
                <design-select
                  :key="`Design_Select_${color}x${windowWidth}`"
                  v-model="design"
                  class="my-4"
                  :options="designOptions"
                  :base-tag-svg-img-path="baseTagSvgImgPath"
                />
              </ValidationObserver>
            </section>
            <!-- Step 3 (Add Text & Select Font for Step 2 Selection) -->
            <section v-show="currentStep === 3">
              <ValidationObserver ref="p_style_form_step_3" slim>
                <design-font-select v-model="designFont" class="mb-4" />
                <design-text
                  v-model="designText"
                  class="my-4"
                  :text-before-valid="!!color && !!size"
                  :show-text2="!!designTextConfig.phoneText"
                  @increaseFontSize="increaseFontSize"
                  @reduceFontSize="reduceFontSize"
                  @moveUp="moveUp"
                  @moveDown="moveDown"
                  @moveLeft="moveLeft"
                  @moveRight="moveRight"
                />
              </ValidationObserver>
            </section>
          </section>
          <!-- Step 2 | 4 (DYO) (Add Personalization) -->
          <section v-show="!isDYOT ? true : currentStep === 4">
            <ValidationObserver :ref="`p_style_form_step_${!isDYOT ? 2 : 4}`" v-slot="{ validated }" slim>
              <personalize
                :key="`Preview_Back_${currentStep}x${windowWidth}x${personalize.editTag}`"
                v-model="personalize"
                :pet-known="!!requiredPet"
                :pet="requiredPet ? requiredPet : null"
                :is-logged-in="!!user && !!user.id"
                :pet-owner="order ? order.petOwner : null"
                :base-svg-img-path="baseTagSvgImgPath"
                :back-svg-img-path="backSvgImgPath"
                :default-phone-number="defaultPhoneNumber"
                :show-redesigned-tag-back="showRedesignedTagBack"
                :left-zone-line1="tagBackLeftZoneLine1"
                :left-zone-line2="tagBackLeftZoneLine2"
                :hotline-number="tagBackHotlineNumber"
                :tag-back-url="tagBackUrl"
                class="pt-2 mb-4"
                :preview-height="backPreviewHeight"
                :preview-scale="0.9"
                :is-admin="isAdmin"
                :hide-preview="isAdmin || !isDYOT"
                :price="personalizationPrice"
                :validated="validated"
                @assignToPet="assignToPet"
              />
            </ValidationObserver>
          </section>
          <!-- Step 3 | 5 (DYO) (Add Glow Tag and/or Select Connector) -->
          <section v-show="!isDYOT ? true : currentStep === 5">
            <ValidationObserver :ref="`p_style_form_step_${!isDYOT ? 3 : 5}`" v-slot="{ validated }" slim>
              <section v-if="isDYOT">
                <b-row class="custom-design-background" no-gutters>
                  <b-col>
                    <custom-design
                      ref="Preview_Front"
                      :key="`Preview_Front_${currentStep}x${windowWidth}`"
                      v-model="designerModeConfig"
                      title="FRONT"
                      :base-tag-svg-img-path="baseTagSvgImgPath ? getImage(baseTagSvgImgPath) : false"
                      :design-svg-img-path="designSvgImgPath ? getImage(designSvgImgPath) : false"
                      :pet-name-text="designTextConfig.petNameText"
                      :phone-text="designTextConfig.phoneText"
                      :home-safe-id-text="designTextConfig.homeSafeIdText || false"
                      :scale="0.8"
                      :exportable="true"
                      :height="250"
                      :boundary-padding="25"
                      canvas-b-g-color="#f7f7f7"
                      :preview="true"
                    />
                  </b-col>
                  <b-col>
                    <personalize
                      :key="`Preview_Back_${currentStep}x${windowWidth}`"
                      v-model="personalize"
                      :pet="requiredPet ? requiredPet : null"
                      :pet-owner="order ? order.petOwner : null"
                      :pet-known="!!requiredPet"
                      :is-logged-in="!!user && !!user.id"
                      :preview-only="true"
                      :preview-height="250"
                      :is-admin="isAdmin"
                      :preview-show-labels="currentStep !== totalSteps"
                      :base-svg-img-path="baseTagSvgImgPath"
                      :back-svg-img-path="backSvgImgPath"
                      :default-phone-number="defaultPhoneNumber"
                      :show-redesigned-tag-back="showRedesignedTagBack"
                      :left-zone-line1="tagBackLeftZoneLine1"
                      :left-zone-line2="tagBackLeftZoneLine2"
                      :hotline-number="tagBackHotlineNumber"
                      :tag-back-url="tagBackUrl"
                      :price="personalizationPrice"
                      :validated="validated"
                      @assignToPet="assignToPet"
                    />
                  </b-col>
                </b-row>
              </section>
              <h1 v-if="currentStep === 5" class="text-center font-weight-bold my-4">{{ $t('dp.generic.almostDone') }}</h1>
              <glow-tag-product-select
                v-show="!isAdmin && showGlowTagProduct"
                ref="glow-tag-product-select"
                v-model="glowTagQuantity"
                :glow-tag-product-id="getGlowTagProductId()"
                class="mb-4"
              />
              <connector-select
                v-show="showConnectorProduct"
                ref="connector-select"
                v-model="connector"
                :is-admin="isAdmin"
                :options="connectorOptions"
                class="mb-4"
              />
              <key-chain-product-select
                v-show="!isAdmin && showKeychainProduct"
                v-if="!disable.large"
                ref="key-chain-product-select"
                v-model="keyChain"
                :key-chain-product="keyChainProduct"
                class="mb-4"
              />
              <wallet-card-product-select
                v-if="showWalletCardCTA"
                ref="wallet-card-product-select"
                v-model="optInForWalletCard"
                :image="walletCardImage"
                :wallet-card-branding="walletCardBranding"
                class="mb-4 pt-2"
              />
            </ValidationObserver>
          </section>
          <!-- Step 4 | 5 (WalletCard) (Add a personalized wallet card) -->
          <Steps
            v-if="!isAdmin && isDYOT"
            class="fixed-bottom"
            :current-step="currentStep"
            :total-steps="totalSteps"
            :is-button-disabled="itemLoading"
            @prevClick="prevClick"
            @nextClick="nextClick"
          >
            <template #prev-btn-content>
              <section v-if="currentStep !== totalSteps">&lt; {{ $t('dp.generic.previous') }}</section>
              <section v-else @click="currentStep = 1">&lt; {{ $t('editTag') }}</section>
            </template>
            <template #next-btn-content>
              <section v-if="currentStep !== totalSteps">{{ $t('dp.generic.next') }}</section>
              <section v-else>
                {{ $t('product.addToBasket') }}
                <div v-if="itemLoading" role="status" class="fa-pulse float-right mt-1">
                  <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
                  <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
                </div>
              </section>
            </template>
          </Steps>

          <product-bundle-sidebar-element
            v-if="!forceHideBundledSidebar && !isEditOrderItemBundleTag && showProductBundleSidebarElement"
            id="product-bundle-sidebar"
            :context="productStyle.productBundleSidebarElement"
            :submit-disabled="allOutOfStock || itemLoading"
            :item-loading="itemLoading"
            class="mt-4"
            @submit="validateBeforeAddBundle"
          />

          <b-button
            v-if="isAdmin || !isDYOT"
            id="product-style-add-to-basket"
            v-testid="'tag-add-to-basket-action'"
            variant="primary"
            :disabled="allOutOfStock || itemLoading"
            block
            class="rounded-8px"
            @click="validateBeforeAddItem"
          >
            {{ editTagRehydrated ? $t('product.updateTag') : !isAdmin ? submitButtonComputedText : $t('product.addToOrder') }}
            <div v-if="itemLoading" role="status" class="fa-pulse float-right mt-1">
              <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
              <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
            </div>
          </b-button>

          <!-- DISCLAIMER -->
          <small v-if="productStyle && productStyle.disclaimer" class="d-block mt-3" v-html="productStyle.disclaimer" />
        </form>
      </ValidationObserver>
    </dp-container>
    <dp-container v-else>
      <section class="mb-3">
        <b-link :to="{ name: 'Store' }" class="text-decoration-none" v-text="'< Back to Store'" />
      </section>
      <div id="not-found" class="mt-5 mb-0">
        <b-card overlay :img-src="require('@/assets/images/404.jpg')" img-fluid img-alt="" class="border-0">
          <b-card-title class="d-none d-lg-block display-4 font-weight-bold mt-4">{{ $t('dp.productNotFound.notFound') }}</b-card-title>
          <b-card-title class="d-block d-lg-none h2 font-weight-bold mt-3">{{ $t('dp.productNotFound.notFound') }}</b-card-title>
          <b-card-body class="mt-lg-5">
            <b-button :to="{ name: 'TagStore' }" class="px-5" variant="primary">{{ $t('dp.productNotFound.buttons.viewAll') }}</b-button>
          </b-card-body>
        </b-card>
      </div>
    </dp-container>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { DpContainer, DpLoading, DpFocusableElementMixins, DpHomeSafeBrandingMixin } from '@docupet/component-library';
import Carousel from '@/components/Carousel';
import SizeSelect from '@/components/SizeSelect';
import ColorSelect from '@/components/ColorSelect';
import ConnectorSelect from '@/components/ConnectorSelect';
import Personalize from '@/components/Personalize';
import PetHeader from '@/components/PetHeader';
import { NotificationMixins } from '@/mixins/notification';
import DesignSelect from '../components/DesignSelect';
import DesignFontSelect from '../components/DesignFontSelect';
import CustomDesign from '../components/CustomDesign';
import getImage from '../mixins/getImage';
import DesignText from '../components/DesignText';
import Steps from '../components/library/Steps';
import GlowTagProductSelect from '../components/GlowTagProductSelect';
import KeyChainProductSelect from '../components/KeyChainProductSelect';
import WalletCardProductSelect from '@/components/WalletCardProductSelect';
import { ProductStyleAnalyticsMixins } from '@/mixins/analytics/productStyle';
import { ProductRedirectMixins } from '@/mixins/redirect';
import ProductConfigBackLink from '@/components/common/ProductConfigBackLink';
import ProductBundleSidebarElement from '@/components/ProductBundleSidebarElement.vue';

export default {
  name: 'Style',
  components: {
    ProductBundleSidebarElement,
    ProductConfigBackLink,
    GlowTagProductSelect,
    KeyChainProductSelect,
    WalletCardProductSelect,
    Steps,
    DesignText,
    CustomDesign,
    DesignFontSelect,
    DesignSelect,
    DpContainer,
    DpLoading,
    Carousel,
    SizeSelect,
    ColorSelect,
    ConnectorSelect,
    Personalize,
    PetHeader,
  },
  mixins: [
    NotificationMixins,
    getImage,
    ProductStyleAnalyticsMixins,
    ProductRedirectMixins,
    DpFocusableElementMixins,
    DpHomeSafeBrandingMixin,
  ],
  props: {
    id: {
      type: [String, Number],
      default: '',
      required: false,
    },
    petId: {
      type: [String, Number],
      default: '',
      required: false,
    },
  },
  data() {
    return {
      size: '',
      color: '',
      design: '',
      designText: {
        text1: 'Text Line 1',
        text2: 'Optional Line 2',
        text3: '',
      },
      designFont: { fontFamily: 'Bebas Neue' },
      personalize: {
        selected: false,
        valid: true,
        name: '',
        phone: '',
      },
      connector: '',
      pet: '',
      glowTagQuantity: 0,
      keyChain: false,
      optInForWalletCard: false,
      currentStep: 1,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      uploadedExportDesigns: null,
      designerModeConfig: null,
      editTagRehydrated: false,
      ready: false,
      forceHideBundledSidebar: false,
    };
  },
  metaTag() {
    return {
      ...this.$router.currentRoute.meta,
      title: `${this.productStyle?.name || ''} -`,
      meta: [
        { charset: 'utf-8' },
        {
          vmid: 'description',
          name: 'description',
          content: this.productStyle?.description || '',
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.productStyle?.description || '',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      city: (state) => state.user.city,
      cityId: (state) => state.cityId,
      orderId: (state) => state.orderId,
      baseUrl: (state) => state.baseUrl,
      loading: (state) => state.tagProduct.loading || state.user.loading || state.order.loading || state.user.cityLoading,
      itemLoading: (state) => state.order.itemLoading,
      productStyle: (state) => state.tagProduct.style,
      isProductStyleFree: (state) => state.tagProduct.style.code === 'FREEREG',
      replacementTagStyle: (state) => state.tagProduct.replacementTagStyle,
      connectorOptions: (state) => state.tagProduct.connectors?.filter((connector) => connector?.status === 'I'),
      order: (state) => state.order.order,
      user: (state) => state.user.user,
      isAdmin: (state) => state.user.user.id && state.user.user.__typename === 'Administrator',
      isPartnerOrShelterAdmin: (state) =>
        state.user.user.id &&
        state.user.user.__typename === 'Administrator' &&
        ['ROLE_PARTNER_ADMIN', 'ROLE_SHELTER_ADMIN'].some((role) => state.user.user.roles?.includes(role)),
      getLocale: (state) => state.locale,
    }),
    ...mapGetters({
      getRequiredPetByPetId: 'order/getRequiredPetByPetId',
      generateFullUrl: 'generateFullUrl',
      localBasketCount: 'order/getLocalCount',
      isNasnCohort: 'isNasnCohort',
    }),
    // NASN cohort: this page is per-pet (pet id is in the route), so the copy
    // reflects the single required pet.
    nasnPetName() {
      return this.requiredPet?.name || '';
    },
    // NASN cohort: replace the standard product header (avatar, back link,
    // product name/description, price) with the Figma "[Pet Name]'s Free Tag"
    // welcome header. Only shown for the free HomeSafe registration tag.
    showNasnStyleHeader() {
      return this.isNasnCohort && this.isProductStyleFree && !this.isAdmin && !!this.requiredPet;
    },
    // Height of the mobile-layout back-of-tag preview (Add Personalization step).
    // The instructional labels render taller than the canvas artwork's bounding box, so
    // wider canvases need more vertical room to keep the lowest label clear of the "BACK"
    // title. Values are the minimum verified to clear that overlap per breakpoint (small
    // phones need >=290 at ~400px, so 300 is the floor here — do not lower). Desktop uses
    // a separate preview and is unaffected.
    backPreviewHeight() {
      if (this.windowWidth <= 400) return 300;
      if (this.windowWidth >= 600) return 540;
      return 360;
    },
    productStyleName() {
      return this.formatHomeSafeText(this.productStyle.name);
    },
    productStyleDescription() {
      return this.formatHomeSafeText(this.productStyle.description);
    },
    productBundleCallToActionText() {
      if (!this.showProductBundleSidebarElement) {
        return null;
      }

      const totalSavings = this.$n(this.productStyle.productBundleSidebarElement.totalSavingsLabel, 'currency');

      if (this.requiredPet) {
        return this.$t('product.productBundleCallToActionPetKnown', {
          petName: this.requiredPet.name,
          totalSavings,
        });
      }

      return this.$t('product.productBundleCallToActionPetUnknown', { totalSavings });
    },
    requiredPet() {
      return this.getRequiredPetByPetId(
        this.petId,
        !(this.isAdmin && (this.$router?.currentRoute?.query?.single || this.$router?.currentRoute?.query?.['replacement-tag']))
      );
    },
    replacementTagStatus() {
      return this.$router?.currentRoute?.query?.['replacement-tag'];
    },
    tag() {
      return this.selectedProduct?.id || '';
    },
    selectedProduct() {
      return this.productStyle?.products?.find(
        (product) =>
          product.color === this.color && ((product.size === '3/L' && this.size === 'L') || (product.size === '1/S' && this.size === 'S'))
      );
    },
    completed() {
      return this.tag && this.personalize.valid;
    },
    images() {
      let images = [this.productStyle.image, ...this.productStyle.images];
      this.productStyle.products.forEach((product) => {
        if (product.size == '3/L') images.push(product.image);
      });
      return images.filter((image) => !!image && !!image?.original);
    },
    curImageIdx() {
      // If color is selected
      if (this.color) {
        let selectedColorProducts = this.productStyle.products.filter(({ color }) => color == this.color);
        // Find index of selected color product image
        return this.images.findIndex((image) => image.original == selectedColorProducts[0].image.original);
      } else {
        return 0;
      }
    },
    colorOptions() {
      let colors = [];
      this.productStyle.products.forEach((product) => {
        let index = colors.findIndex((color) => color.name == product.color);
        if (index == -1) {
          index = colors.length;
          colors.push({ name: product.color });
        }

        if (product.size == '1/S') {
          colors[index].smallInStock = product.status === 'I';
        } else if (product.size == '3/L') {
          colors[index].image = product.image;
          colors[index].backImage = product.backImage;
          colors[index].largeInStock = product.status === 'I';
          colors[index].material = product.material?.material ?? product.laserPrinterJig?.material;
          colors[index].shape = product.laserPrinterJig?.shape;
        }
      });
      return colors;
    },
    totalSteps() {
      return this.isDYOT && this.windowWidth <= 991 ? 5 : 3;
    },
    isDYOT() {
      return this.productStyle?.designs && this.productStyle?.designs?.length !== 0;
    },
    baseTagSvgImgPath() {
      return this.isDYOT ? this.colorOptions?.find((color) => color.name == this.color)?.image?.original : false;
      // return null;
    },
    designSvgImgPath() {
      return this.designOptions?.find((design) => design.title == this.design)?.image || null;
    },
    backSvgImgPath() {
      return this.colorOptions?.find((color) => color.name == this.color)?.backImage?.original || null;
      // return null;
    },
    showRedesignedTagBack() {
      return !!this.city?.enableQrCodeTagBack;
    },
    tagBackLeftZoneLine1() {
      return this.order?.jurisdiction?.tagBackLeftZoneLine1 ?? 'Scan here';
    },
    tagBackLeftZoneLine2() {
      return this.order?.jurisdiction?.tagBackLeftZoneLine2 ?? null;
    },
    tagBackHotlineNumber() {
      return this.city?.tagBackHotlineNumber ?? '';
    },
    tagBackUrl() {
      return this.city?.tagBackUrl ?? 'www.docupet.com';
    },
    designOptions() {
      let designs = [];
      this.productStyle?.designs?.forEach((design) => {
        let index = designs.findIndex((d) => d.title == design.title);
        if (index == -1) {
          index = designs.length;
          designs.push({
            title: design.title,
            image: design.designSVGFile.original,
            category: design.designCategory,
            sort: design.qualification || 1,
          });
        }
      });
      // Sort designs by qualification
      return designs.sort((a, b) => (a.sort > b.sort ? 1 : -1));
    },
    designTextConfig() {
      let textConfig = {
        petNameText: {
          offsetX: 0,
          offsetY: 0,
          boundaryPadding: 40 + Number(this.designFont?.extraBoundaryPadding || 0),
          color: 'black',
          text: this.designText.text1,
          minFontSize: 20,
          maxFontSize: 400,
          maxCharacters: 20,
          heightScaleFactor: 1.2,
          ...this.designFont,
          ...this.designFont?.petNameText,
          options: {
            originY: 'bottom',
          },
        },
        phoneText: {
          offsetX: 0,
          offsetY: Number(-5 + Number(this.designFont?.phoneText?.relativeOffsetY || 0)),
          boundaryPadding: 40 + Number(this.designFont?.extraBoundaryPadding || 0),
          color: 'black',
          text: this.designText.text2,
          minFontSize: 20,
          maxFontSize: 400,
          maxCharacters: 20,
          heightScaleFactor: 0.8,
          ...this.designFont,
          ...this.designFont?.phoneText,
          options: {
            originY: 'top',
          },
        },
      };

      if (this.design) {
        const selectedDesign = this.productStyle?.designs?.find((design) => design.title == this.design);
        textConfig.petNameText = {
          ...textConfig.petNameText,
          offsetX: selectedDesign?.designText1PositionOffsetX,
          offsetY: selectedDesign?.designText1PositionOffsetY,
          boundaryPadding: Number(selectedDesign?.designText1BoundaryPadding) + Number(this.designFont?.extraBoundaryPadding || 0),
          color: selectedDesign?.designText1Color,
          heightScaleFactor: selectedDesign?.designText1HeightScaleFactor,
        };
        textConfig.phoneText =
          !selectedDesign || selectedDesign?.designText2
            ? {
                ...textConfig.phoneText,
                offsetX: selectedDesign?.designText2PositionOffsetX,
                offsetY: selectedDesign?.designText2PositionOffsetY,
                boundaryPadding: Number(selectedDesign?.designText2BoundaryPadding) + Number(this.designFont?.extraBoundaryPadding || 0),
                color: selectedDesign?.designText2Color,
                heightScaleFactor: selectedDesign?.designText2HeightScaleFactor,
              }
            : false;
      }

      if (!this.designerModeConfig?.designerMode) {
        // Two lines of text
        if (textConfig.phoneText && textConfig.phoneText.text !== '') {
          textConfig.petNameText.groupCenterY = textConfig.phoneText.groupCenterY = textConfig.petNameText.offsetY;
          textConfig.petNameText.offsetY = -Number(Number(textConfig.petNameText.groupCenterY) + Number(textConfig.phoneText.offsetY) / 2);
          textConfig.phoneText.offsetY = -Number(Number(textConfig.phoneText.groupCenterY) - Number(textConfig.phoneText.offsetY) / 2);
          textConfig.petNameText.heightScaleFactor = textConfig.phoneText.options.heightScaleFactor = Number(
            textConfig.phoneText.heightScaleFactor || textConfig.petNameText.heightScaleFactor || 0.85
          );
        }
        // One line of text
        else {
          textConfig.petNameText.options.originY = 'center';
          textConfig.petNameText.offsetY = -Number(textConfig.petNameText.offsetY);
          textConfig.petNameText.heightScaleFactor = Number(textConfig.petNameText.heightScaleFactor || 2);
        }
      }

      if (textConfig.petNameText && this.designerModeConfig?.petNameText && this.designerModeConfig?.designerMode)
        textConfig.petNameText = { ...textConfig.petNameText, ...this.designerModeConfig.petNameText };
      if (textConfig.phoneText && this.designerModeConfig?.phoneText && this.designerModeConfig?.designerMode)
        textConfig.phoneText = { ...textConfig.phoneText, ...this.designerModeConfig.phoneText };

      if (this.designFont?.callback !== null && this.designFont.callback instanceof Function) this.designFont.callback(textConfig);

      return textConfig;
    },
    disable() {
      let disable = { small: false, large: false };
      let color = this.colorOptions?.find((color) => color.name == this.color);
      disable.small = !color || !color.smallInStock;
      disable.large = !color || !color.largeInStock;
      return disable;
    },
    editTag() {
      // Check if it's edit tag?
      let query = this.$router.currentRoute?.query;
      return query?.etid
        ? this.order?.items?.find(
            (item) =>
              item?.id == query?.etid &&
              ['DESIGNER_TAG', 'PET_TAG'].includes(item?.product?.type) &&
              item?.group?.pet?.id == this.petId &&
              item?.product?.style?.id == this.id
          )
        : null;
    },
    defaultPhoneNumber() {
      return this.city?.customerService?.phoneNumber?.replace(/-/, '')?.substring(1) || '';
    },
    keyChainProduct() {
      return this.productStyle?.products?.find((product) => product?.size === '3/L');
    },
    showGlowTagProduct() {
      return this.colorOptions.every((color) => color.shape === 'RND') && !this.isEditOrderItemBundleTag;
    },
    personalizationPrice() {
      return this.selectedProduct?.personalization?.price ?? this.productStyle?.products?.[0]?.personalization?.price ?? 5;
    },
    allOutOfStock() {
      return this.colorOptions.every((color) => !color.smallInStock && !color.largeInStock);
    },
    walletCardBranding() {
      if (this.city) {
        return {
          header: this.city.branding?.walletCardInterstitialHeaderHtml,
          description: this.city.branding?.walletCardInterstitialDescriptionHtml,
          checkboxDescription: this.city.branding?.walletCardInterstitialCheckboxDescriptionHtml,
        };
      }
      return null;
    },
    walletCardImage() {
      return this.getLocale === 'en_US'
        ? require('@/assets/images/wallets/optInImage-us.png')
        : require('@/assets/images/wallets/optInImage-ca.png');
    },
    showWalletCardProducts() {
      return this.city?.showWalletCardProducts && !this.isEditOrderItemBundleTag;
    },
    showKeychainProduct() {
      return this.city?.tagConfigEnableKeychainAddon && !this.isEditOrderItemBundleTag;
    },
    isUser() {
      return Boolean(this.user?.id);
    },
    showWalletCardCTA() {
      return this.showWalletCardProducts && this.requiredPet && !this.requiredPet?.walletCardOptedIn && !this.isAdmin && this.isUser;
    },
    disableTagPersonalizationByDefault() {
      return this.city?.isTagPersonalizationDisabledByDefault;
    },
    canSkipLicensing() {
      return this.requiredPet?.licensing && (this.requiredPet?.hasPreviousTag || this.requiredPet?.hasPreviousMunicipalTag);
    },
    isReplacementTag() {
      return this.$router?.currentRoute?.query?.['replacement-tag'];
    },
    showSkipReplacementTagBanner() {
      return !this.loading && this.ready && this.canSkipLicensing && !this.isAdmin;
    },
    submitButtonComputedText() {
      if (this.editTagRehydrated) {
        return this.$t('product.updateTag');
      }

      if (this.isAdmin) {
        return this.$t('product.addToOrder');
      }

      if (this.showProductBundleSidebarElement) {
        return this.$t('product.addTagToBasket');
      }

      return this.$t('product.addToBasket');
    },
    editOrderItemBundleId() {
      return this.editTag?.orderItemBundle?.id || null;
    },
    isEditOrderItemBundleTag() {
      return this.editOrderItemBundleId !== null && !this.isAdmin;
    },
    showProductBundleSidebarElement() {
      return this.productStyle?.productBundleSidebarElement && !(this.editTag && !this.isEditOrderItemBundleTag);
    },
    showConnectorProduct() {
      return !this.isEditOrderItemBundleTag;
    },
  },
  watch: {
    selectedProduct: async function (newValue, oldValue) {
      if (newValue == oldValue) {
        return;
      }

      await this.fetchProductBundleSidebarElement();
    },
    'personalize.selected': async function () {
      await this.fetchProductBundleSidebarElement();
    },
    design(newDesign, oldDesign) {
      if (newDesign !== oldDesign) {
        this.designerModeConfig = null;
      }
    },
    designFont(newFont, oldFont) {
      if (newFont?.fontFamily !== oldFont?.fontFamily) {
        this.designerModeConfig = null;
      }
    },
  },
  created() {
    let stylesLoaded = false;
    (async () => {
      stylesLoaded = true;
      if (this.$route.query['replacement-tag'] === 'true' && this.petId) {
        this.$store.dispatch('tagProduct/fetchReplacementTagStyle', { id: this.$route.params.id, cityId: this.cityId, petId: this.petId });
      } else {
        this.$store.dispatch('tagProduct/fetchStyle', { id: this.$route.params.id, cityId: this.cityId });
      }
      while (this.loading) await new Promise((resolve) => setTimeout(resolve, 1000));
    })().then(() => {
      if (this.petId && !!this.order?.id && !this.order.groups?.map(({ pet }) => pet?.id)?.includes(this.petId)) {
        this.$router.push({ name: 'TagStore' });
      }
      if (!stylesLoaded) {
        if (this.$route.query['replacement-tag'] === 'true' && this.petId) {
          this.$store.dispatch('tagProduct/fetchReplacementTagStyle', {
            id: this.$route.params.id,
            cityId: this.cityId,
            petId: this.petId,
          });
        } else {
          this.$store.dispatch('tagProduct/fetchStyle', { id: this.$route.params.id, cityId: this.cityId });
        }
      }

      if (this.isProductStyleFree && this.isPartnerOrShelterAdmin) {
        if (this.petId) {
          this.$router.push({ name: 'TagsForPet', petId: this.petId });
        } else {
          this.$router.push({ name: 'Tags' });
        }
      }

      this.ready = true;
      // Set Personalization
      this.personalize.name = this.requiredPet?.name || '';
      if (!this.personalize.phone || this.personalize.phone === this.defaultPhoneNumber?.toString()?.replaceAll('-', '')) {
        this.personalize.phone = this.order?.petOwner?.primaryPhone || this.defaultPhoneNumber?.toString()?.replaceAll('-', '');
      }

      this.personalize.selected = !this.disableTagPersonalizationByDefault;

      if (this.showProductBundleSidebarElement) {
        this.connector = this.getLessPriceConnectorProduct()?.id || '';
      }
    });
    window.addEventListener('resize', this.windowResize);
    window.addEventListener('hashchange', this.onHashChange);
  },
  updated() {
    window.location = `#${this.currentStep}`;
    if (this.editTag && !this.editTagRehydrated) {
      this.rehydrateEditTag();
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (!this.editTag && !this.disableTagPersonalizationByDefault) {
        this.$set(this.personalize, 'selected', true);
      }
    });
    this.$root?.$children?.[0]?.$refs?.['footer']?.$el?.setAttribute('hidden', window.innerWidth <= 991 ? 'true' : 'false');
    this.rehydrateEditTag();
  },
  destroyed() {
    this.$root?.$children?.[0]?.$refs?.['footer']?.$el?.setAttribute('hidden', 'false');
    this.$store.dispatch('tagProduct/removeStyle', { vm: this });
    window.removeEventListener('resize', this.windowResize);
    window.removeEventListener('hashchange', this.onHashChange);
  },
  methods: {
    async fetchProductBundleSidebarElement() {
      if (this.productStyle?.id == null || this.productStyle?.productBundleSidebarElement == null) {
        return;
      }

      await this.$store.dispatch('tagProduct/fetchProductBundleSidebarElement', {
        tagProductStyleId: this.productStyle.id,
        tagProductId: this.selectedProduct?.id,
        includePersonalization: this.personalize.selected,
      });
    },
    orderNoTag() {
      this.$store.dispatch('order/orderNoTag', { orderId: this.order.id, petId: this.requiredPet.id }).then(() => {
        // Honor a guided flow (e.g. the NASN tag loop via redirect_path=tag-loop) so skipping
        // a pet advances to the next pet instead of dropping out to the tag store.
        if (this.determineRedirectRoute) {
          this.$router.push({ name: this.determineRedirectRoute });
        } else if (this.requiredPet && this.requiredPet?.licensing && !this.isAdmin) {
          this.$router.push({ name: 'ContinueToLicensing' });
        } else {
          this.$router.push({
            name: this.isAdmin && !this.getRequiredPetByPetId() ? 'AdminRedirect' : 'TagStore',
            query: { ...this.$router.currentRoute.query },
          });
        }
      });
    },
    rehydrateEditTag() {
      if (this.editTag) {
        this.$nextTick(() => {
          // Auto-set size
          if (this.editTag?.product?.size) {
            // Safe way to set size when ref is mounted
            const sizeSelectRefInterval = setInterval(() => {
              if (this.$refs['size-select']) {
                if (this.editTag?.product?.size?.includes('S') && !this.disable?.small) this.size = 'S';
                if (this.editTag?.product?.size?.includes('L') && !this.disable?.large) this.size = 'L';
                clearInterval(sizeSelectRefInterval);
              }
            }, 50);
          }
          // Auto-set personalize details
          if (this.editTag?.customTagOrderInfo?.name || this.editTag?.customTagOrderInfo?.phone) {
            this.personalize.editTag = true;
            this.personalize.selected = true;
            this.personalize.name = this.editTag?.customTagOrderInfo?.name;
            this.personalize.phone = this.editTag?.customTagOrderInfo?.phone;
          } else this.personalize.selected = false;
          // Auto-set connector
          let editTagConnectorId = this.editTag?.children?.find((childItem) => childItem?.product?.type == 'CONNECTOR')?.product?.id;
          if (editTagConnectorId) {
            this.connector = editTagConnectorId;
          }
          // Auto-set glow tag
          this.glowTagQuantity = this.editTag?.children?.filter(
            (childItem) => childItem?.product?.type == 'OTHER' && [3284, 3290, 3291].includes(Number(childItem?.product?.id))
          )?.length;
          // Auto set keychain
          this.keyChain = this.editTag?.children?.some((childItem) => childItem?.keyChain);
          this.editTagRehydrated = true;
        });
      }
    },
    async nextClick() {
      this.$refs?.[`p_style_form_step_${this.currentStep}`]?.validate().then(async (success) => {
        if (!success) {
          this.scrollToError(this.$refs?.[`p_style_form_step_${this.currentStep}`]);
        } else this.currentStep < this.totalSteps || !this.completed ? this.currentStep++ : await this.validateBeforeAddItem();
      });
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    },
    prevClick() {
      if (this.currentStep > 1) this.currentStep--;
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    },
    onHashChange(hash) {
      if (hash.isTrusted) {
        const oldHash = hash?.oldURL?.toString()?.split('#')?.[1],
          newHash = hash?.newURL?.toString()?.split('#')?.[1];
        if (oldHash === '1' && !newHash) {
          this.$router.push(!this.petId ? { name: 'TagStore' } : { name: 'StoreForPet', params: { petId: this.petId } });
        } else {
          if (oldHash && newHash && Number(newHash) !== this.currentStep) {
            if (Number(newHash) > Number(oldHash)) this.nextClick();
            else if (Number(newHash) < Number(oldHash)) this.prevClick();
          }
        }
      }
    },
    windowResize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
      this.$root?.$children?.[0]?.$refs?.['footer']?.$el?.setAttribute('hidden', this.windowWidth <= 991 ? 'true' : 'false');
    },
    assignToPet(selectedPet) {
      if (!selectedPet?.id) return;
      this.pet = selectedPet;
    },
    onColorChange() {
      this.size = '';
      if (!this.disable.small && this.disable.large) this.size = 'S';
      if (this.disable.small && !this.disable.large) this.size = 'L';
    },
    async validateBeforeAddBundle() {
      window.dataLayer.push({
        event: 'tag_configuration_bundle_add_bundle_to_basket',
      });

      const formStep1 = this.$refs.p_style_form_step_1;
      const formStep2 = this.$refs.p_style_form_step_2;

      const formStep1Validated = await formStep1?.validate();
      const formStep2Validated = await formStep2?.validate();

      if (!formStep1Validated) {
        this.scrollToError(formStep1);
        return;
      }

      if (!formStep2Validated) {
        this.scrollToError(formStep2);
        return;
      }

      await this.addProductBundle();
    },
    async addProductBundle() {
      let personalization = null;

      if (this.personalize.selected) {
        personalization = {
          name: this.personalize.name,
          phone: this.personalize.phone,
        };
      }

      await this.$store.dispatch('order/addProductBundle', {
        vm: this,
        orderId: this.orderId,
        tagProductId: this.tag,
        petId: this.requiredPet ? this.requiredPet.id : this.pet?.id,
        personalization,
      });

      // Honor a guided flow (e.g. the NASN tag loop via redirect_path=tag-loop) so adding a
      // bundle loops to the next pet instead of dropping out to the tag store / basket.
      if (this.determineRedirectRoute) {
        await this.$router.push({ name: this.determineRedirectRoute });
      } else if (this.requiredPet && this.requiredPet?.licensing && !this.isAdmin) {
        await this.$router.push({ name: 'ContinueToLicensing' });
      } else {
        await this.$router.push({ name: 'TagStore' });
        await this.$store.dispatch('basket/showBasket', true);
      }
    },
    async updateProductBundle() {
      // This is a workaround to hide the bundled sidebar when editing an order item bundle since etid item is removed and computed is re-evaluated before redirect.
      this.forceHideBundledSidebar = true;

      let personalization = null;

      if (this.personalize.selected) {
        personalization = {
          name: this.personalize.name,
          phone: this.personalize.phone,
        };
      }

      await this.$store.dispatch('order/updateProductBundle', {
        vm: this,
        orderId: this.orderId,
        tagProductId: this.tag,
        orderItemBundleId: this.editOrderItemBundleId,
        petId: this.requiredPet ? this.requiredPet.id : this.pet?.id,
        personalization,
      });

      await this.$router.push({ name: 'Basket' });
    },
    async validateBeforeAddItem() {
      const success = await this.$refs.p_style_form?.validate();

      if (!success || !this.completed) {
        this.scrollToError(this.$refs.p_style_form);
        return;
      }

      await this.$nextTick();

      if (this.isEditOrderItemBundleTag) {
        this.updateProductBundle();
        return;
      }

      this.addToBasket();
    },
    addToBasket() {
      if (this.editTag?.id)
        this.$store.dispatch('order/removeItem', { orderId: this.orderId, itemId: this.editTag?.id }).then(() => this.addItem());
      else this.isDYOT && this.$refs['Preview_Front'] ? this.uploadExportDesignsAndAddItem() : this.addItem();
    },
    uploadExportDesignsAndAddItem() {
      this.$store
        .dispatch('order/uploadExportedDesigns', {
          tagFront: this.$refs['Preview_Front']?.exportCanvasPreview(),
          laserFront: this.$refs['Preview_Front']?.exportCanvasLaserFront(),
          svgLaserFront: this.$refs['Preview_Front']?.exportCanvasLaserFrontSvg(),
        })
        .then((response) => {
          this.uploadedExportDesigns = response;
          this.addItem();
        })
        .catch(console.error);
    },
    increaseFontSize(lineNo) {
      this.$refs['Preview_Front']?.increaseFontSize(lineNo);
    },
    reduceFontSize(lineNo) {
      this.$refs['Preview_Front']?.reduceFontSize(lineNo);
    },
    moveUp(lineNo) {
      this.$refs['Preview_Front']?.moveUp(lineNo);
    },
    moveDown(lineNo) {
      this.$refs['Preview_Front']?.moveDown(lineNo);
    },
    moveLeft(lineNo) {
      this.$refs['Preview_Front']?.moveLeft(lineNo);
    },
    moveRight(lineNo) {
      this.$refs['Preview_Front']?.moveRight(lineNo);
    },
    addItem() {
      this.analyticsTrackAddItem();
      const isSplitRingConnector = Number(this.connectorOptions?.find((connector) => connector.id === this.connector)?.price) === 0;
      if (this.user && this.user.id) {
        let details = {
          productId: this.tag,
          children:
            this.connector || this.glowTagQuantity > 0
              ? [
                  ...(this.connector && !isSplitRingConnector
                    ? [
                        {
                          productId: this.connector,
                        },
                      ]
                    : []),
                  ...Array(this.glowTagQuantity).fill({
                    productId: this.getGlowTagProductId(),
                  }),
                ]
              : null,
          petId: this.requiredPet ? this.requiredPet.id : this.pet?.id,
          personalization: this.personalize.selected
            ? {
                name: this.personalize.name,
                phone: this.personalize.phone,
              }
            : null,
          designExports: this.uploadedExportDesigns,
          keyChain: this.keyChain,
          optInForWalletCard: this.optInForWalletCard ? true : null,
        };

        let addItemRedirectTo = null;
        if (
          (this.getRequiredPetByPetId() == null ||
            this.$router?.currentRoute?.query?.single ||
            this.$router?.currentRoute?.query?.['replacement-tag']) &&
          this.isAdmin
        ) {
          addItemRedirectTo = { name: 'AdminRedirect' };
        } else if (this.requiredPet && this.requiredPet?.licensing && !this.isAdmin) {
          addItemRedirectTo = { name: this.requiredPet?.hasTag ? 'Basket' : 'ContinueToLicensing' };
        } else {
          addItemRedirectTo =
            (!this.petId || this.isAdmin || this.$router?.currentRoute?.query?.['side-basket']) &&
            !this.$router?.currentRoute?.query?.['replacement-tag']
              ? { name: 'TagStore' }
              : { name: 'Basket' };
        }
        // Honor a guided flow (e.g. the NASN tag loop via redirect_path=tag-loop) so adding a
        // tag loops to the next pet. Applies to every non-admin path, not just licensing pets,
        // which is why FREE HomeSafe / designer tags (non-licensing) previously broke the loop.
        if (this.determineRedirectRoute && addItemRedirectTo?.name !== 'AdminRedirect') {
          addItemRedirectTo = { name: this.determineRedirectRoute };
        }
        if (['ContinueToLicensing'].includes(addItemRedirectTo?.name)) {
          this.$store.dispatch('basket/showBasket', false);
        }

        this.$store
          .dispatch(this.isReplacementTag ? 'order/addReplacementTagItem' : 'order/addItem', {
            vm: this,
            orderId: this.orderId,
            productDetails: details,
            fetchOrderOnSuccess: !['AdminRedirect', 'ContinueToLicensing'].includes(addItemRedirectTo?.name),
          })
          .then(() => {
            if (addItemRedirectTo) {
              this.$router.push(addItemRedirectTo);
              if (
                !this.$router?.currentRoute?.query?.['replacement-tag'] &&
                !this.determineRedirectRoute &&
                !['ContinueToLicensing'].includes(addItemRedirectTo?.name)
              ) {
                this.$emit('showSideBasket');
              }
            }
          });
      } else {
        const self = this;
        const data = {
          style: this.$route.params.id,
          productId: this.tag,
          color: this.$data.color,
          connector: this.$data.connector,
          personalize: this.$data.personalize,
          petId: this.$data.pet?.id,
          size: this.$data.size,
          otherProductIds: [...Array(this.glowTagQuantity).fill(this.getGlowTagProductId())],
          keyChain: this.keyChain,
          isSplitRingConnector: isSplitRingConnector,
        };
        if (this.isDYOT) {
          data['design'] = this.$data.design || 'None';
          data['designFont'] = this.$data.designFont?.name;
          data['designText'] = this.$data.designText;
          data['designExports'] = this.uploadedExportDesigns;
        }
        this.$store.dispatch('order/storeLocalItems', { data: [data] }).then(() => {
          this.notifySuccess(
            `Item added. <br class="d-block d-md-none" /><a href="${this.generateFullUrl('/basket')}" class="text-primary"><u>${self.$t(
              'basket.viewBasket'
            )}</u></a> | <a href="${this.generateFullUrl(`/login/store?redirect_path=%2Fstore`)}" class="text-primary"><u>${self.$t(
              'basket.checkout'
            )}</u></a>`,
            {
              family: 'AddItem',
              dismissSecs: 10,
              dismissible: true,
              isHtmlMessage: true,
              class: 'text-center',
              text: { class: 'text-secondary' },
              $callback: this,
              callback: function ({ localBasketCount, generateFullUrl, notifySuccess }) {
                notifySuccess(
                  `${localBasketCount} ${localBasketCount > 1 ? 'Items' : 'Item'}. <a href="${generateFullUrl(
                    '/basket'
                  )}" class="text-primary"><u>${self.$t('basket.viewBasket')}</u></a> | <a href="${generateFullUrl(
                    `/login/store?redirect_path=%2Fstore`
                  )}" class="text-primary"><u>${self.$t('basket.checkout')}</u></a>`,
                  {
                    family: 'AddItem',
                    icon: null,
                    dismissSecs: -1,
                    dismissible: true,
                    isHtmlMessage: true,
                    class: 'text-center',
                    text: { class: 'text-secondary' },
                  }
                );
              },
            }
          );
        });
        this.$router.push({ name: 'TagStore' });
        this.$emit('showSideBasket');
      }
    },
    getGlowTagProductId() {
      if (!this.color) return null;
      const colorProduct = this.colorOptions?.find((color) => color.name === this.color);
      if (!colorProduct || colorProduct.shape !== 'RND') {
        return null;
      }
      const GLOW_TAG_PRODUCT = {
        // Any/No material large
        LG: '3284',
        // Small Aluminium (ALM)
        SM_ALM: '3290',
        // Any/No material other than aluminum (ALM)
        SM: '3291',
      };
      return this.size === 'S' ? (colorProduct?.material === 'ALM' ? GLOW_TAG_PRODUCT.SM_ALM : GLOW_TAG_PRODUCT.SM) : GLOW_TAG_PRODUCT.LG;
    },
    analyticsTrackAddItem() {
      if (window?.dataLayer && this.selectedProduct) {
        window?.dataLayer?.push({
          ecommerce: null,
        });
        const items = [];
        const _itemData = {
          quantity: 1,
          coupon: this.order?.discount?.code || '',
          currency: `${this.order?.city?.country || 'US'}D`,
          item_list_id: this.productStyle?.id || '',
          item_list_name: this.productStyle?.code?.toUpperCase() || '',
        };
        if (this.personalize?.selected) {
          items.push({
            ..._itemData,
            item_id: `${this.selectedProduct?.type?.toUpperCase()}_PERSONALIZATION`,
            item_name: `${this.selectedProduct?.type?.toUpperCase()}_PERSONALIZATION`,
            item_category: 'PERSONALIZATION',
            price: Number(this.selectedProduct?.personalization?.price || 0).toFixed(2),
          });
        }
        const glowTagProduct = this.$refs?.['glow-tag-product-select']?.glowTagProduct;
        if (this.glowTagQuantity > 0 && this.getGlowTagProductId() && glowTagProduct) {
          items.push({
            ..._itemData,
            quantity: this.glowTagQuantity,
            item_id: this.getGlowTagProductId() || '',
            item_name: glowTagProduct?.name?.toUpperCase() || '',
            item_category: glowTagProduct?.type?.toUpperCase() || '',
            price: Number(
              this.glowTagQuantity > 1 ? glowTagProduct?.discountedPrice * this.glowTagQuantity : glowTagProduct?.price || 0
            ).toFixed(2),
          });
        }
        const connectorProduct = this.connectorOptions?.find((connector) => connector.id === this.connector);
        if (connectorProduct?.price > 0) {
          items.push({
            ..._itemData,
            item_id: connectorProduct?.id || '',
            item_name: connectorProduct?.name?.toUpperCase() || '',
            item_category: 'CONNECTOR',
            price: Number(connectorProduct?.price || 0).toFixed(2),
          });
        }
        if (this.keyChain && this.keyChainProduct) {
          items.push({
            ..._itemData,
            item_id: `${this.keyChainProduct?.id}_MATCHING_KEYCHAIN`,
            item_name: `${this.keyChainProduct?.id}_MATCHING_KEYCHAIN`,
            item_category: 'MATCHING_KEYCHAIN',
            price: Number(this.keyChainProduct?.keyChainDiscountedPrice || this.keyChainProduct?.keyChainPrice || 0).toFixed(2),
          });
        }
        items.push({
          ..._itemData,
          item_id: this.selectedProduct?.id || '',
          item_name: this.selectedProduct?.name?.toUpperCase() || '',
          item_category: this.selectedProduct?.type?.toUpperCase() || '',
          price: Number(this.selectedProduct?.discountedPrice || this.selectedProduct?.price || 0).toFixed(2),
        });
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            items: items,
            currency: `${this.order?.city?.country || 'US'}D`,
            value: Number(items?.reduce((p, c) => p + Number(c.price), 0)).toFixed(2),
          },
        });
      }
    },
    getLessPriceConnectorProduct() {
      return this.connectorOptions?.sort((a, b) => (a.price > b.price ? 1 : -1))?.[0];
    },
    bundleComponentCreated() {
      window.dataLayer.push({
        event: 'tag_configuration_bundle_view',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.dp-container-wrapper::v-deep .row .dp-container {
  overflow: visible;
}
.mx-n3-5 {
  margin-left: -1.25rem !important;
  margin-right: -1.25rem !important;
}
#multi-tag-bottom {
  padding-bottom: 0.5rem;
  margin-bottom: -2.9rem;
}
#product-group {
  margin-top: -6.2rem;
}
.sticky {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  margin-top: 0;
  padding-top: 0;
  z-index: 999;
}
#not-found {
  text-align: center;
  svg {
    color: $dp-gray-2;
    width: 35%;
    height: 35%;
  }
  h2 {
    margin-top: 2rem;
    color: $dp-gray-3;
  }
}
.disabled {
  pointer-events: none;
}
.custom-design-background {
  background-color: $dp-gray-1;
}
// NASN cohort: Figma "[Pet Name]'s Free Tag" welcome header
.nasn-style-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 16px 16px;
  text-align: center;

  &__title {
    font-weight: 700;
    font-size: 24px;
    line-height: 1.25;
    color: $dp-navy;
    margin: 0;
  }

  &__subtitle {
    font-size: 16px;
    line-height: 1.5;
    color: $neutral-700;
    margin: 0;

    ::v-deep .nasn-style-header__disclaimer {
      display: block;
      margin-top: 16px;
      font-size: 14px;
      font-style: italic;
    }
  }
}
</style>
<style lang="scss">
.po-user {
  .--admin-only {
    display: none;
  }
  .--po-only {
    display: block;
  }
}
.admin-user {
  .--admin-only {
    display: block;
  }
  .--po-only {
    display: none;
  }
}

.rounded-8px {
  border-radius: 8px !important;
}
</style>
